import { Bus, type SoundBusManager } from '@src/audio/SoundBusManager';
import { floorThemeDef } from '@src/game/arena/FloorThemes';

const BPM = 128;
const STEP = 60 / BPM / 4;
const LOOKAHEAD = 0.12;
const TICK_MS = 25;
const THEME_COUNT = 3;

interface ThemeBed {
  chords: ReadonlyArray<readonly number[]>;
  bass: readonly number[];
  bassAlt: readonly number[];
  riff: readonly number[];
  padCutoff: number;
  padGain: number;
  hatCutoff: number;
  heavyKickAt: number;
  stinger: readonly number[];
}

/** Obsidian (Am F C G), verdant (F C D Am), infernal (E Phrygian). Same BPM, different color. */
const THEMES: readonly ThemeBed[] = [
  {
    chords: [
      [110.0, 130.81, 164.81],
      [87.31, 110.0, 130.81],
      [130.81, 164.81, 196.0],
      [98.0, 123.47, 146.83],
    ],
    bass: [0, 0, 1, 0, 2, 0, 1, 1, 0, 0, 1, 0, 2, 1, 0, 1],
    bassAlt: [0, 2, 0, 1, 0, 0, 2, 1, 0, 1, 0, 2, 1, 0, 2, 0],
    riff: [1, 0, 1, 0, 1, 1, 0, 1, 1, 0, 1, 0, 1, 1, 1, 0],
    padCutoff: 1400,
    padGain: 0.06,
    hatCutoff: 7000,
    heavyKickAt: 0.6,
    stinger: [164.81, 220.0, 329.63],
  },
  {
    chords: [
      [87.31, 110.0, 130.81],
      [130.81, 164.81, 196.0],
      [146.83, 174.61, 220.0],
      [110.0, 130.81, 164.81],
    ],
    bass: [0, 1, 0, 2, 0, 1, 2, 0, 0, 2, 1, 0, 2, 0, 1, 1],
    bassAlt: [0, 0, 2, 1, 0, 1, 0, 2, 1, 0, 2, 0, 0, 1, 2, 1],
    riff: [1, 0, 0, 1, 1, 0, 1, 0, 1, 0, 0, 1, 1, 1, 0, 1],
    padCutoff: 1800,
    padGain: 0.07,
    hatCutoff: 8200,
    heavyKickAt: 0.55,
    stinger: [174.61, 220.0, 261.63],
  },
  {
    chords: [
      [82.41, 98.0, 123.47],
      [87.31, 103.83, 130.81],
      [98.0, 116.54, 146.83],
      [110.0, 130.81, 155.56],
    ],
    bass: [0, 0, 0, 1, 2, 0, 1, 0, 0, 2, 0, 1, 2, 2, 0, 1],
    bassAlt: [0, 1, 0, 0, 2, 2, 0, 1, 0, 0, 1, 2, 0, 2, 1, 0],
    riff: [1, 1, 0, 1, 0, 1, 1, 0, 1, 0, 1, 1, 0, 1, 1, 1],
    padCutoff: 900,
    padGain: 0.05,
    hatCutoff: 5200,
    heavyKickAt: 0.42,
    stinger: [155.56, 185.0, 220.0],
  },
];

/**
 * Procedural arcade loop: kick, snare, hats, saw bass, detuned pad. A heavy
 * layer (distorted riff, double kick, screaming choir) fades in with `intensity`.
 * Floor theme maps onto three procedural beds (obsidian / verdant / infernal)
 * and swaps the chord bed on the next bar; room enters fire a short stinger.
 * Music stays on the Music bus so announcer ducking still wins.
 */
export class MusicEngine {
  private readonly ctx: AudioContext;
  private readonly out: GainNode;
  private readonly heavy: GainNode;
  private readonly noise: AudioBuffer;
  private readonly shaper: WaveShaperNode;
  private timer = 0;
  private nextStepTime = 0;
  private step = 0;
  private cycles = 0;
  private running = false;
  private intensity = 0;
  private theme = 0;
  private pendingTheme = 0;
  /** Per-room color: drum pattern and lead motif re-rolled on each room enter. */
  private drumVariant = 0;
  private motif = 0;
  /** Steps left in a drop-out break (kick, bass, riff rest; hats and pad carry on). */
  private breakSteps = 0;
  private breakPending = false;
  private pad: { oscs: OscillatorNode[]; gain: GainNode; filter: BiquadFilterNode } | null = null;
  private choir: { oscs: OscillatorNode[]; gain: GainNode } | null = null;
  private boss = false;
  private drone: { oscs: OscillatorNode[]; gain: GainNode } | null = null;
  private intro: { oscs: OscillatorNode[]; gain: GainNode; filter: BiquadFilterNode } | null = null;
  /** Start-screen theme (procedural fallback when public/assets/music/menu.mp3 is missing). */
  private menu: { oscs: OscillatorNode[]; gain: GainNode; choirGain: GainNode; mix: GainNode; timer: number; nextTime: number; step: number } | null = null;
  private streamed = false;

  constructor(buses: SoundBusManager) {
    this.ctx = buses.context;
    this.out = this.ctx.createGain();
    this.out.gain.value = 0.9;
    this.out.connect(buses.bus(Bus.Music));
    this.heavy = this.ctx.createGain();
    this.heavy.gain.value = 0;
    this.heavy.connect(this.out);

    this.noise = this.ctx.createBuffer(1, this.ctx.sampleRate, this.ctx.sampleRate);
    const d = this.noise.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;

    this.shaper = this.ctx.createWaveShaper();
    const curve = new Float32Array(1024);
    for (let i = 0; i < curve.length; i++) {
      const x = (i / (curve.length - 1)) * 2 - 1;
      curve[i] = Math.tanh(x * 4.5);
    }
    this.shaper.curve = curve;
    this.shaper.connect(this.heavy);
  }

  isRunning(): boolean {
    return this.running;
  }

  /** Soft duck of the procedural output (pause menu). `level` is 0..1 relative to normal. */
  setDuck(level: number, seconds = 0.25): void {
    const target = Math.max(0.0001, 0.9 * Math.max(0, Math.min(1, level)));
    const t = this.ctx.currentTime;
    this.out.gain.cancelScheduledValues(t);
    this.out.gain.setValueAtTime(Math.max(0.0001, this.out.gain.value), t);
    this.out.gain.setTargetAtTime(target, t, Math.max(0.04, seconds * 0.35));
  }

  start(): void {
    if (this.running) return;
    this.stopIntro();
    this.stopMenu(0.35);
    this.disconnectPadChoir();
    this.running = true;
    this.streamed = false;
    this.breakPending = false;
    this.breakSteps = 0;
    const t = this.ctx.currentTime;
    this.out.gain.cancelScheduledValues(t);
    this.out.gain.setValueAtTime(Math.max(0.0001, this.out.gain.value), t);
    this.out.gain.exponentialRampToValueAtTime(0.9, t + 0.22);
    this.nextStepTime = t + 0.05;
    this.step = 0;
    this.startPad();
    this.startChoir();
    this.timer = window.setInterval(() => this.schedule(), TICK_MS);
  }

  /** Park the arena loop: scheduler off, pad/choir torn down, output faded. Safe to `start()` again. */
  stop(fade = 0.2): void {
    this.running = false;
    window.clearInterval(this.timer);
    this.timer = 0;
    this.stopIntro();
    this.stopMenu(fade);
    this.stopDrone(this.ctx.currentTime);
    this.boss = false;
    this.intensity = 0;
    this.breakPending = false;
    this.breakSteps = 0;
    const t = this.ctx.currentTime;
    this.heavy.gain.cancelScheduledValues(t);
    this.heavy.gain.setTargetAtTime(0, t, 0.06);
    this.out.gain.cancelScheduledValues(t);
    this.out.gain.setValueAtTime(Math.max(0.0001, this.out.gain.value), t);
    this.out.gain.exponentialRampToValueAtTime(0.0001, t + Math.max(0.05, fade));
    this.disconnectPadChoir();
    this.streamed = false;
  }

  /** Menu one-shots ride the menu mix so `stopMenu` kills taiko/brass tails with the bed. */
  private dest(): AudioNode {
    if (this.menu) return this.menu.mix;
    return this.out;
  }

  private disconnectPadChoir(): void {
    const t = this.ctx.currentTime;
    const layers = [this.pad, this.choir];
    this.pad = null;
    this.choir = null;
    for (const layer of layers) {
      if (!layer) continue;
      try {
        layer.gain.disconnect();
      } catch {
        // already disconnected
      }
      for (const o of layer.oscs) {
        try {
          o.stop(t + 0.02);
        } catch {
          // already stopped
        }
      }
    }
  }

  /**
   * A streamed track (MusicTracks) has taken over: mute the procedural output so
   * the two never stack. The scheduler keeps running so handing back is seamless.
   */
  setStreamed(on: boolean): void {
    if (on === this.streamed) return;
    this.streamed = on;
    const t = this.ctx.currentTime;
    this.out.gain.cancelScheduledValues(t);
    this.out.gain.setValueAtTime(Math.max(0.0001, this.out.gain.value), t);
    // Mute fast so a streamed bed never sits on top of the procedural loop.
    this.out.gain.exponentialRampToValueAtTime(on ? 0.0001 : 0.9, t + (on ? 0.12 : 0.45));
  }

  /**
   * Start-screen theme: 72 BPM epic bed. Sub drone and a wide saw pad walk the
   * obsidian progression two bars per chord; taiko hits on 1 and 3 with a roll into
   * every fourth bar, a tolling bell, a brass fanfare motif on the turnarounds and a
   * choir that swells across the eight-bar phrase. Fades under `start()`/`startIntro()`.
   */
  startMenu(): void {
    if (this.menu || this.running || this.intro) return;
    const t = this.ctx.currentTime;
    const gain = this.ctx.createGain();
    gain.gain.value = 0;
    gain.gain.setTargetAtTime(0.13, t, 1.6);
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 900;
    filter.Q.value = 0.8;
    const lfo = this.ctx.createOscillator();
    lfo.frequency.value = 0.09;
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = 320;
    lfo.connect(lfoGain);
    lfoGain.connect(filter.frequency);
    lfo.start(t);
    const oscs: OscillatorNode[] = [lfo];
    // Sub, then six detuned saws (pad), then the choir stack through its own bandpass.
    const sub = this.ctx.createOscillator();
    sub.type = 'sine';
    sub.connect(gain);
    sub.start(t);
    oscs.push(sub);
    for (let i = 0; i < 6; i++) {
      const o = this.ctx.createOscillator();
      o.type = 'sawtooth';
      o.detune.value = (i % 2 === 0 ? -1 : 1) * (6 + i * 2);
      o.connect(filter);
      o.start(t);
      oscs.push(o);
    }
    filter.connect(gain);
    const choirGain = this.ctx.createGain();
    choirGain.gain.value = 0;
    const cf = this.ctx.createBiquadFilter();
    cf.type = 'bandpass';
    cf.frequency.value = 1300;
    cf.Q.value = 1.2;
    const vib = this.ctx.createOscillator();
    vib.frequency.value = 4.8;
    const vibGain = this.ctx.createGain();
    vibGain.gain.value = 7;
    vib.connect(vibGain);
    vib.start(t);
    oscs.push(vib);
    for (let i = 0; i < 4; i++) {
      const o = this.ctx.createOscillator();
      o.type = 'sawtooth';
      o.detune.value = (i - 1.5) * 8;
      vibGain.connect(o.detune);
      o.connect(cf);
      o.start(t);
      oscs.push(o);
    }
    const mix = this.ctx.createGain();
    mix.gain.value = 1;
    mix.connect(this.out);
    cf.connect(choirGain);
    choirGain.connect(mix);
    gain.connect(mix);
    const menu = { oscs, gain, choirGain, mix, timer: 0, nextTime: t + 0.1, step: 0 };
    this.menu = menu;
    this.menuChord(0, t);
    menu.timer = window.setInterval(() => this.scheduleMenu(), TICK_MS);
  }

  stopMenu(fade = 1.2): void {
    const m = this.menu;
    if (!m) return;
    this.menu = null;
    window.clearInterval(m.timer);
    const t = this.ctx.currentTime;
    const kill = Math.max(0.05, fade);
    try {
      m.mix.gain.cancelScheduledValues(t);
      m.mix.gain.setValueAtTime(Math.max(0.0001, m.mix.gain.value), t);
      m.mix.gain.exponentialRampToValueAtTime(0.0001, t + kill);
    } catch {
      // AudioParam may already be torn down
    }
    for (const o of m.oscs) {
      try {
        o.stop(t + kill + 0.2);
      } catch {
        // already stopped
      }
    }
  }

  private menuChord(bar: number, t: number): void {
    const m = this.menu;
    if (!m) return;
    const chord = THEMES[0].chords[Math.floor(bar / 2) % 4];
    // oscs: [lfo, sub, pad x6, vib, choir x4]
    m.oscs[1].frequency.setTargetAtTime(chord[0] * 0.5, t, 0.08);
    for (let i = 0; i < 6; i++) m.oscs[2 + i].frequency.setTargetAtTime(chord[i % 3] * (i >= 3 ? 2 : 1), t, 0.06);
    for (let i = 0; i < 4; i++) m.oscs[9 + i].frequency.setTargetAtTime(chord[i % 3] * 4, t, 0.1);
  }

  private scheduleMenu(): void {
    const m = this.menu;
    if (!m) return;
    const step = 60 / 72 / 4;
    while (m.nextTime < this.ctx.currentTime + LOOKAHEAD) {
      this.playMenuStep(m.step, m.nextTime, step);
      m.nextTime += step;
      m.step = (m.step + 1) % 128;
    }
  }

  private playMenuStep(step: number, t: number, len: number): void {
    const m = this.menu;
    if (!m) return;
    const bar = Math.floor(step / 16);
    const s = step % 16;
    const chord = THEMES[0].chords[Math.floor(bar / 2) % 4];
    const phrase = bar / 8;
    if (s === 0) {
      this.menuChord(bar, t);
      m.choirGain.gain.setTargetAtTime(0.02 + phrase * 0.075, t, 1.2);
      if (bar % 4 === 0) this.bell(t, chord[0] * 2, bar === 0 ? 0.22 : 0.15);
    }
    // Taiko: 1 and 3, a ghost on the "and" of 4, a roll into every fourth bar.
    if (s === 0) this.kick(t, 0.95);
    if (s === 8) this.kick(t, 0.75);
    if (s === 14 && bar % 2 === 1) this.kick(t, 0.45);
    if (bar % 4 === 3 && s >= 10) this.tom(t, 0.25 + (s - 10) * 0.08, 170 - (s - 10) * 14);
    if (s === 12 && bar >= 4) this.snare(t, 0.22);
    // Fanfare motif on the turnaround bars: root, fifth, octave, held.
    if (bar % 4 === 2 && (s === 0 || s === 3 || s === 6)) {
      const note = s === 0 ? chord[0] : s === 3 ? chord[2] : chord[0] * 2;
      this.brass(t, note, len * (s === 6 ? 9 : 3), bar >= 4 ? 0.2 : 0.14);
    }
    if (bar % 4 === 3 && s === 0) this.brass(t, chord[0] * 2, len * 12, 0.18);
    // Slow arpeggio on 8ths from the second phrase, sitting under the brass.
    if (bar >= 4 && s % 2 === 0) {
      const idx = (s / 2) % 4;
      const note = idx === 3 ? chord[0] * 4 : chord[idx] * 2;
      this.pluck(t, note, len * 1.8, 0.08);
    }
  }

  /** Brass-ish: three detuned saws with a slow attack through a warm lowpass. */
  private brass(t: number, freq: number, dur: number, vol: number): void {
    const f = this.ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(700, t);
    f.frequency.linearRampToValueAtTime(2200, t + 0.12);
    f.frequency.exponentialRampToValueAtTime(600, t + dur);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + 0.06);
    g.gain.setValueAtTime(vol, t + dur * 0.7);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    f.connect(g);
    g.connect(this.dest());
    for (const detune of [-7, 0, 7]) {
      const o = this.ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = freq;
      o.detune.value = detune;
      o.connect(f);
      o.start(t);
      o.stop(t + dur + 0.05);
    }
  }

  private pluck(t: number, freq: number, dur: number, vol: number): void {
    const o = this.ctx.createOscillator();
    o.type = 'triangle';
    o.frequency.value = freq;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g);
    g.connect(this.dest());
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  /**
   * Opening cinematic bed: sub drone plus a slow, dark pad on the obsidian
   * chord, no drums. `setIntroBuild` opens the filter and adds a heartbeat as
   * the show builds; `start()` fades it under the first bar of the arena loop.
   */
  isIntro(): boolean {
    return this.intro !== null;
  }

  startIntro(): void {
    if (this.intro || this.running) return;
    this.stopMenu(0.32);
    const t = this.ctx.currentTime;
    this.out.gain.cancelScheduledValues(t);
    this.out.gain.setValueAtTime(Math.max(0.0001, this.out.gain.value), t);
    this.out.gain.exponentialRampToValueAtTime(0.9, t + 0.2);
    const chord = THEMES[0].chords[0];
    const gain = this.ctx.createGain();
    gain.gain.value = 0;
    gain.gain.setTargetAtTime(0.11, t, 1.4);
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 240;
    filter.Q.value = 0.9;
    const oscs: OscillatorNode[] = [];
    const voices: Array<[OscillatorType, number, number]> = [
      ['sine', chord[0] * 0.5, 0],
      ['sawtooth', chord[0], -6],
      ['sawtooth', chord[0], 6],
      ['sawtooth', chord[1], 4],
      ['triangle', chord[2], -4],
      ['sawtooth', chord[0] * 2, 9],
    ];
    for (const [type, freq, detune] of voices) {
      const o = this.ctx.createOscillator();
      o.type = type;
      o.frequency.value = freq;
      o.detune.value = detune;
      o.connect(filter);
      o.start(t);
      oscs.push(o);
    }
    const lfo = this.ctx.createOscillator();
    lfo.frequency.value = 0.13;
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = 70;
    lfo.connect(lfoGain);
    lfoGain.connect(filter.frequency);
    lfo.start(t);
    oscs.push(lfo);
    filter.connect(gain);
    gain.connect(this.out);
    this.intro = { oscs, gain, filter };
  }

  /** 0 = cold open murmur, 1 = about to hand off to the arena loop. */
  setIntroBuild(v: number): void {
    if (!this.intro) return;
    const b = Math.max(0, Math.min(1, v));
    const t = this.ctx.currentTime;
    this.intro.filter.frequency.setTargetAtTime(240 + b * b * 2600, t, 0.6);
    this.intro.gain.gain.setTargetAtTime(0.11 + b * 0.09, t, 1.2);
  }

  /** One slow heartbeat kick plus a low sting in the bed's key; cinematic beat markers. */
  introPulse(vol = 0.5): void {
    if (!this.intro) return;
    const t = this.ctx.currentTime + 0.02;
    this.kick(t, vol);
    this.kick(t + 0.36, vol * 0.7);
    this.sting(t, 0.16);
  }

  private stopIntro(): void {
    const n = this.intro;
    if (!n) return;
    this.intro = null;
    const t = this.ctx.currentTime;
    n.gain.gain.cancelScheduledValues(t);
    n.gain.gain.setValueAtTime(n.gain.gain.value, t);
    n.gain.gain.setTargetAtTime(0, t, 0.4);
    for (const o of n.oscs) o.stop(t + 2);
  }

  /** 0 = quiet room, 1 = 300 monsters. Pinned to 1 while a boss is on stage. */
  setIntensity(v: number): void {
    const clamped = this.boss ? 1 : Math.max(0, Math.min(1, v));
    if (Math.abs(clamped - this.intensity) < 0.01) return;
    this.intensity = clamped;
    const heavy = clamped * clamped;
    this.heavy.gain.setTargetAtTime(heavy * 0.7, this.ctx.currentTime, 0.4);
    this.choir?.gain.gain.setTargetAtTime(Math.max(0, clamped - 0.55) * 0.5, this.ctx.currentTime, 0.6);
  }

  /**
   * Boss layer: half-time drums, a driving 16th ostinato on the chord root with a
   * chromatic snarl, a tolling bell each bar and a sub drone. Switches on the
   * next step; the drone fades in and out so the handoff is not a hard cut.
   */
  setBoss(active: boolean): void {
    if (active === this.boss) return;
    this.boss = active;
    const t = this.ctx.currentTime;
    if (active) {
      this.breakPending = false;
      this.breakSteps = 0;
      this.setIntensity(1);
      this.startDrone(t);
    } else {
      this.stopDrone(t);
      this.intensity = -1;
      this.setIntensity(0);
    }
  }

  private startDrone(t: number): void {
    if (this.drone) return;
    const gain = this.ctx.createGain();
    gain.gain.value = 0;
    gain.gain.setTargetAtTime(0.16, t, 1.2);
    const f = this.ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 160;
    const oscs: OscillatorNode[] = [];
    const root = this.bed().chords[0][0] * 0.5;
    const voices: Array<[OscillatorType, number, number]> = [
      ['sine', root, 0],
      ['sawtooth', root, -5],
      ['sawtooth', root, 5],
      ['square', root * 0.5, 0],
    ];
    for (const [type, freq, detune] of voices) {
      const o = this.ctx.createOscillator();
      o.type = type;
      o.frequency.value = freq;
      o.detune.value = detune;
      o.connect(f);
      o.start(t);
      oscs.push(o);
    }
    f.connect(gain);
    gain.connect(this.out);
    this.drone = { oscs, gain };
  }

  private stopDrone(t: number): void {
    const n = this.drone;
    if (!n) return;
    this.drone = null;
    n.gain.gain.cancelScheduledValues(t);
    n.gain.gain.setValueAtTime(n.gain.gain.value, t);
    n.gain.gain.setTargetAtTime(0, t, 0.5);
    for (const o of n.oscs) o.stop(t + 2.5);
  }

  /** Half-time: kick on 1 and 3, a crack on 3, driving 8th hats, a tom roll into the bar. */
  private bossDrums(s: number, t: number, hatCutoff: number): void {
    if (s === 0 || s === 8) this.kick(t, 1);
    if (s === 6) this.kick(t, 0.55, true);
    if (s === 8) this.snare(t, 0.7);
    if (s === 12 && this.cycles % 2 === 1) this.snare(t, 0.3);
    if (s % 2 === 1) this.hat(t, s % 4 === 3 ? 0.2 : 0.12, hatCutoff);
    if (s >= 13) this.tom(t, 0.35 + (s - 13) * 0.12, 180 - (s - 13) * 30);
  }

  /** Root-heavy 16ths with a chromatic snarl (b2) and an octave leap at the turnaround. */
  private bossRiffNote(chord: readonly number[], s: number, bar: number): number {
    const root = chord[0];
    if (s === 3 || s === 11) return root * 1.0595;
    if (s === 7) return chord[1];
    if (s === 14 || s === 15) return bar === 3 ? root * 2 : chord[2];
    return root;
  }

  private tom(t: number, vol: number, freq: number): void {
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, t);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.55, t + 0.14);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.2);
    osc.connect(g);
    g.connect(this.dest());
    osc.start(t);
    osc.stop(t + 0.22);
  }

  /** Tolling bell: two inharmonic partials with a long tail, once per bar in boss mode. */
  private bell(t: number, freq: number, vol: number): void {
    const partials: Array<[number, number, number]> = [
      [1, 1, 1.8],
      [2.76, 0.45, 1.2],
      [5.4, 0.2, 0.6],
    ];
    for (const [ratio, amp, tail] of partials) {
      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq * ratio;
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(vol * amp, t);
      g.gain.exponentialRampToValueAtTime(0.0001, t + tail);
      osc.connect(g);
      g.connect(this.dest());
      osc.start(t);
      osc.stop(t + tail + 0.05);
    }
  }

  /** Procedural bed follows the floor's mapped color (obsidian / verdant / infernal). */
  setThemeFromFloor(floor: number): void {
    const next = floorThemeDef(floor).procBed % THEME_COUNT;
    if (next === this.pendingTheme) return;
    this.pendingTheme = next;
  }

  /** @deprecated Use setThemeFromFloor. */
  setThemeFromTier(tier: number): void {
    this.setThemeFromFloor(Math.max(1, Math.floor((Math.max(0, tier - 1) / 3) + 1)));
  }

  /** Short theme-colored sting on room enter. Rides the Music bus (ducked by Vaelen). */
  accent(): void {
    if (!this.running) return;
    this.sting(this.ctx.currentTime + 0.03, 0.26);
    this.rerollRoomColor();
  }

  /** Occasional one-bar drop-out after a room clears, so the loop breathes between fights. */
  roomBreak(): void {
    if (!this.running || Math.random() > 0.55) return;
    this.breakPending = true;
  }

  private rerollRoomColor(): void {
    const drum = (this.drumVariant + 1 + Math.floor(Math.random() * 2)) % 3;
    const motif = (this.motif + 1 + Math.floor(Math.random() * 2)) % 3;
    this.drumVariant = drum;
    this.motif = motif;
  }

  private riffNote(chord: readonly number[], s: number): number {
    const root = chord[0];
    if (this.motif === 1) {
      if (s % 8 === 6) return chord[1];
      if (s === 14) return chord[2];
      return root;
    }
    if (this.motif === 2) {
      if (s % 4 === 2) return root * 2;
      if (s === 15) return chord[1] * 2;
      return root;
    }
    return s % 8 === 6 ? root * 2 : root;
  }

  private drums(s: number, t: number, hatCutoff: number): void {
    const resting = this.breakSteps > 0;
    if (this.drumVariant === 1) {
      if (!resting && (s % 4 === 0 || s === 10)) this.kick(t, s === 10 ? 0.7 : 0.9);
      if (s === 4 || s === 12) this.snare(t, 0.5);
      if (s === 14) this.snare(t, 0.2);
      if (s % 2 === 1) this.hat(t, s % 4 === 3 ? 0.18 : 0.1, hatCutoff);
      return;
    }
    if (this.drumVariant === 2) {
      if (!resting && (s === 0 || s === 6 || s === 8 || s === 12)) this.kick(t, 0.9);
      if (s === 4 || s === 12) this.snare(t, 0.5);
      if (s === 7) this.snare(t, 0.25);
      this.hat(t, s % 4 === 3 ? 0.16 : s % 2 === 1 ? 0.09 : 0.05, hatCutoff);
      return;
    }
    if (!resting && s % 4 === 0) this.kick(t, 0.9);
    if (s === 4 || s === 12) this.snare(t, 0.5);
    if (s % 2 === 1) this.hat(t, s % 4 === 3 ? 0.18 : 0.1, hatCutoff);
  }

  private bed(): ThemeBed {
    return THEMES[this.theme];
  }

  private schedule(): void {
    while (this.nextStepTime < this.ctx.currentTime + LOOKAHEAD) {
      this.playStep(this.step, this.nextStepTime);
      this.nextStepTime += STEP;
      this.step += 1;
      if (this.step >= 64) {
        this.step = 0;
        this.cycles += 1;
      }
    }
  }

  private playStep(step: number, t: number): void {
    const bar = Math.floor(step / 16);
    const s = step % 16;
    let switched = false;
    if (s === 0 && this.pendingTheme !== this.theme) {
      this.applyTheme(this.pendingTheme, t);
      switched = true;
    }

    const bed = this.bed();
    const chord = bed.chords[bar];
    const bassPat = this.cycles & 1 ? bed.bassAlt : bed.bass;

    if (s === 0 && this.breakPending) {
      this.breakPending = false;
      this.breakSteps = 16;
    }
    const resting = this.breakSteps > 0;
    if (resting) this.breakSteps--;

    if (this.boss) {
      this.bossDrums(s, t, bed.hatCutoff);
      if (s === 0) this.bell(t, chord[0] * 2, bar === 0 ? 0.2 : 0.12);
      if (s % 4 === 0) this.bass(t, chord[0] * 0.5, 0.36);
      this.riff(t, this.bossRiffNote(chord, s, bar), s % 4 === 0 ? 0.55 : 0.4);
      if (s === 15 && bar === 3) this.updatePadChord(0, t + STEP);
      if (s === 15 && bar !== 3) this.updatePadChord(bar + 1, t + STEP);
      return;
    }

    this.drums(s, t, bed.hatCutoff);
    if (this.theme === 1 && s === 6) this.hat(t, 0.08, bed.hatCutoff);
    if (this.theme === 2 && s === 7) this.snare(t, 0.22);

    if (!resting) {
      const bassNote = chord[bassPat[s]] * (bassPat[s] === 2 ? 0.5 : 1);
      this.bass(t, bassNote, 0.32);
      if (bed.riff[s]) this.riff(t, this.riffNote(chord, s), 0.5);
    } else if (s === 15) {
      // Pickup into the drop back in.
      this.snare(t, 0.35);
      this.kick(t + STEP * 0.5, 0.6);
    }
    if (!resting && s % 2 === 0 && this.intensity > bed.heavyKickAt) this.kick(t + STEP, 0.5, true);
    if (s === 0 && bar === 0 && !switched) this.sting(t, 0.14);
    if (s === 15 && bar === 3) this.updatePadChord((bar + 1) % 4, t + STEP);
    if (s === 15 && bar !== 3) this.updatePadChord(bar + 1, t + STEP);
  }

  private applyTheme(index: number, t: number): void {
    this.theme = index;
    const bed = this.bed();
    if (this.pad) {
      this.pad.filter.frequency.setTargetAtTime(bed.padCutoff, t, 0.12);
      this.pad.gain.gain.setTargetAtTime(0.02, t, 0.06);
      this.pad.gain.gain.setTargetAtTime(bed.padGain, t + 0.22, 0.18);
    }
    this.updatePadChord(0, t);
    this.sting(t, 0.2);
  }

  private sting(t: number, vol: number): void {
    const notes = this.bed().stinger;
    for (let i = 0; i < notes.length; i++) {
      const osc = this.ctx.createOscillator();
      osc.type = i === notes.length - 1 ? 'triangle' : 'sawtooth';
      osc.frequency.value = notes[i];
      const g = this.ctx.createGain();
      const start = t + i * 0.055;
      g.gain.setValueAtTime(vol * 0.35, start);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.22);
      osc.connect(g);
      g.connect(this.dest());
      osc.start(start);
      osc.stop(start + 0.24);
    }
  }

  private kick(t: number, vol: number, heavy = false): void {
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(150, t);
    osc.frequency.exponentialRampToValueAtTime(38, t + 0.12);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
    osc.connect(g);
    g.connect(heavy ? this.heavy : this.dest());
    osc.start(t);
    osc.stop(t + 0.3);
  }

  private snare(t: number, vol: number): void {
    const src = this.ctx.createBufferSource();
    src.buffer = this.noise;
    const f = this.ctx.createBiquadFilter();
    f.type = 'highpass';
    f.frequency.value = 1800;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
    src.connect(f);
    f.connect(g);
    g.connect(this.dest());
    src.start(t, Math.random() * 0.4);
    src.stop(t + 0.2);
  }

  private hat(t: number, vol: number, cutoff: number): void {
    const src = this.ctx.createBufferSource();
    src.buffer = this.noise;
    const f = this.ctx.createBiquadFilter();
    f.type = 'highpass';
    f.frequency.value = cutoff;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
    src.connect(f);
    f.connect(g);
    g.connect(this.dest());
    src.start(t, Math.random() * 0.4);
    src.stop(t + 0.06);
  }

  private bass(t: number, freq: number, vol: number): void {
    const osc = this.ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = freq;
    const f = this.ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(900, t);
    f.frequency.exponentialRampToValueAtTime(220, t + STEP * 0.9);
    f.Q.value = 6;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.setValueAtTime(vol, t + STEP * 0.6);
    g.gain.exponentialRampToValueAtTime(0.0001, t + STEP * 0.95);
    osc.connect(f);
    f.connect(g);
    g.connect(this.out);
    osc.start(t);
    osc.stop(t + STEP);
  }

  private riff(t: number, freq: number, vol: number): void {
    const osc = this.ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = freq * 2;
    const osc2 = this.ctx.createOscillator();
    osc2.type = 'square';
    osc2.frequency.value = freq * 2 * 1.005;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + STEP * 1.8);
    osc.connect(g);
    osc2.connect(g);
    g.connect(this.shaper);
    osc.start(t);
    osc2.start(t);
    osc.stop(t + STEP * 2);
    osc2.stop(t + STEP * 2);
  }

  private startPad(): void {
    const bed = this.bed();
    const gain = this.ctx.createGain();
    gain.gain.value = 0;
    gain.gain.setTargetAtTime(bed.padGain, this.ctx.currentTime, 1.2);
    const f = this.ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = bed.padCutoff;
    const oscs: OscillatorNode[] = [];
    for (let i = 0; i < 6; i++) {
      const o = this.ctx.createOscillator();
      o.type = 'sawtooth';
      o.detune.value = (i % 2 === 0 ? -1 : 1) * 7;
      o.connect(f);
      o.start();
      oscs.push(o);
    }
    f.connect(gain);
    gain.connect(this.out);
    this.pad = { oscs, gain, filter: f };
    this.updatePadChord(0, this.ctx.currentTime);
  }

  private updatePadChord(index: number, t: number): void {
    if (!this.pad) return;
    const chord = this.bed().chords[index];
    for (let i = 0; i < this.pad.oscs.length; i++) {
      const note = chord[i % 3] * (i >= 3 ? 2 : 1) * 2;
      this.pad.oscs[i].frequency.setTargetAtTime(note, t, 0.05);
    }
    if (this.choir) {
      for (let i = 0; i < this.choir.oscs.length; i++) {
        this.choir.oscs[i].frequency.setTargetAtTime(chord[i % 3] * 8, t, 0.08);
      }
    }
  }

  private startChoir(): void {
    const gain = this.ctx.createGain();
    gain.gain.value = 0;
    const f = this.ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = 1600;
    f.Q.value = 1.4;
    const lfo = this.ctx.createOscillator();
    lfo.frequency.value = 5.5;
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = 9;
    lfo.connect(lfoGain);
    lfo.start();
    const oscs: OscillatorNode[] = [lfo];
    for (let i = 0; i < 4; i++) {
      const o = this.ctx.createOscillator();
      o.type = 'sawtooth';
      o.detune.value = (i - 1.5) * 9;
      lfoGain.connect(o.detune);
      o.connect(f);
      o.start();
      oscs.push(o);
    }
    f.connect(gain);
    gain.connect(this.heavy);
    this.choir = { oscs, gain };
  }
}
